import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../../ui/button';
import { IoMdSend } from 'react-icons/io';
import { FaCopy, FaCheck, FaDownload } from 'react-icons/fa';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useQuota } from '../../context/QuotaContext';
import UsageLimits from '../../pages/Protected/Tools/UsageLimits';
import { Link } from 'react-router-dom';
import useThemeColors from '../../hooks/useThemeColors';
import { toast } from 'react-toastify';
import axios from 'axios';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const AIChatModal = ({ isOpen, onClose, onConfirm, title, onTextGenerated }) => {
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
  const [gptLimitReached, setGptLimitReached] = useState(false);
  const messagesContainerRef = useRef(null);
  const { activeWorkspace, idToken } = useWorkspace();
  const { quota, refreshQuota } = useQuota();
  const { isDark } = useThemeColors();

  // Check GPT limit from quota context
  useEffect(() => {
    if (quota?.gpt) {
      const isLimitReached = !quota.gpt.unlimited && quota.gpt.used >= quota.gpt.limit;
      setGptLimitReached(isLimitReached);
    }
  }, [quota]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Reset copied state after a timeout
  useEffect(() => {
    if (copiedMessageIndex !== null) {
      const timer = setTimeout(() => {
        setCopiedMessageIndex(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedMessageIndex]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || loading || gptLimitReached) return;

    const userMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const tempInputValue = inputValue;
    setInputValue('');
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_API_URL}/api/auth/user/ai/conversations`, {
        message: tempInputValue,
        workspaceId: activeWorkspace.id
      }, {
        headers: { Authorization: `Bearer ${idToken}` }
      });

      if (response.data.success && response.data.conversation.messages.length > 1) {
        const aiMessage = response.data.conversation.messages[response.data.conversation.messages.length - 1];
        setMessages(prev => [...prev, aiMessage]);
      }
      
      // Refresh quota after sending a message
      refreshQuota();
    } catch (err) {
      console.error('Error sending message:', err);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for image detection
  const hasImageGenerationFunction = (message) => {
    return message.role === 'assistant' && 
           message.function_call && 
           message.function_call.name === 'generateImage';
  };

  const hasGeneratedImage = (message) => {
    return message.role === 'assistant' && message.generatedImage && message.generatedImage.url;
  };

  const downloadImage = async (imageUrl, prompt) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      // Clean the prompt for filename
      const cleanPrompt = prompt ? prompt.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50) : 'generated_image';
      a.download = `${cleanPrompt}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Image downloaded successfully!');
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error('Failed to download image');
    }
  };

  const handleCopyMessage = async (messageContent, index) => {
    try {
      await navigator.clipboard.writeText(messageContent);
      setCopiedMessageIndex(index);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleConfirmWithMessage = (message) => {
    // Check if the message has a generated image
    const hasImage = hasImageGenerationFunction(message) || hasGeneratedImage(message);
    
    if (hasImage && message.generatedImage) {
      // Return both text and image data
      onConfirm({
        hasImage: true,
        textContent: message.content,
        imageData: message.generatedImage
      });
    } else {
      // Return just the text content (existing behavior)
      onConfirm(message.content);
    }
    onClose();
  };

  const resetModal = () => {
    setMessages([]);
    setInputValue('');
    setLoading(false);
    setCopiedMessageIndex(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 modal-overlay">
      <div className="--theme-colors rounded-lg shadow-md w-full max-w-2xl h-[600px] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b --theme-colors-border">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-semibold --theme-colors">{title || 'AI Assistant'}</h3>
              <p className="text-sm --theme-colors opacity-70 mt-1">Ask AI to help generate content for your post</p>
            </div>
            <div className="flex-shrink-0">
              <UsageLimits 
                showGpt={true}
                showImage={false}
                showUpload={false}
                variant="pill"
              />
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {gptLimitReached && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded mb-4">
              <p className="font-medium">You've reached your GPT message limit!</p>
              <p className="mt-1">To continue using the AI assistant, please add more credits to your account.</p>
              <Link to="/dashboard/settings/subscription" className="mt-2 inline-block bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700">
                Upgrade Subscription
              </Link>
            </div>
          )}
          {messages.length === 0 ? (
            <div className="text-center --theme-colors opacity-70 mt-8">
              <p>Ask me to help generate content for your post!</p>
              <p className="text-sm mt-2">Try: "Create a catchy caption for my video" or "Generate hashtags for fitness content"</p>
            </div>
          ) : (
            messages.map((message, index) => {
              const hasImage = hasImageGenerationFunction(message) || hasGeneratedImage(message);
              
              return (
                <div key={index}>
                  <div
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] p-3 rounded-lg ${
                        message.role === 'user'
                          ? 'bg-blue-500 !text-white'
                          : '--theme-colors border --theme-colors-border'
                      }`}
                    >
                      <div className="whitespace-pre-wrap">{message.content}</div>
                    </div>
                  </div>

                  {/* Generated Image Display */}
                  {hasImage && message.generatedImage && (
                    <div className="mt-3 flex justify-start">
                      <div className="max-w-[80%]">
                        <div className="border --theme-colors-border rounded-lg p-3 --theme-colors">
                          <img 
                            src={message.generatedImage.url} 
                            alt={message.generatedImage.prompt}
                            className="w-full max-w-md rounded-lg shadow-sm"
                            style={{ maxHeight: '400px', objectFit: 'contain' }}
                          />
                          <div className="mt-2 text-sm --theme-colors opacity-75">
                            <div><strong>Size:</strong> {message.generatedImage.size}</div>
                            {message.generatedImage.prompt && (
                              <div><strong>Prompt:</strong> {message.generatedImage.prompt}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action buttons */}
                  {message.role === 'assistant' && (
                    <div className="flex justify-start mt-2">
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyMessage(message.content, index)}
                          className="h-6 px-2 text-xs"
                        >
                          {copiedMessageIndex === index ? (
                            <>
                              <FaCheck size={10} className="mr-1" />
                              Copied
                            </>
                          ) : (
                            <>
                              <FaCopy size={10} className="mr-1" />
                              Copy
                            </>
                          )}
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleConfirmWithMessage(message)}
                          className="h-6 px-2 text-xs"
                        >
                          Use This
                        </Button>
                        {hasImage && message.generatedImage && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadImage(message.generatedImage.url, message.generatedImage.prompt)}
                            className="h-6 px-2 text-xs flex items-center gap-1"
                          >
                            <FaDownload size={10} />
                            Download
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
          {loading && (
            <div className="flex justify-start">
              <div className="--theme-colors border --theme-colors-border p-3 rounded-lg max-w-[250px] flex items-center">
                <div className="text-sm mr-2 --theme-colors">AI is typing</div>
                <div className="flex space-x-1">
                  <div className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-white' : 'bg-gray-500'}`} style={{ animationDelay: '0ms' }}></div>
                  <div className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-white' : 'bg-gray-500'}`} style={{ animationDelay: '150ms' }}></div>
                  <div className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-white' : 'bg-gray-500'}`} style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="p-4 border-t --theme-colors-border">
          <div className="flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={gptLimitReached ? "You've reached your GPT limit. Upgrade to continue..." : "Ask AI to help with your content..."}
              className="flex-1 px-3 py-2 border --theme-colors-border --theme-colors rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading || gptLimitReached}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
            />
            <Button
              variant="primary"
              size="md"
              disabled={!inputValue.trim() || loading || gptLimitReached}
              onClick={handleSendMessage}
            >
              <IoMdSend size={16} />
            </Button>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end gap-3 p-4 border-t --theme-colors-border">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIChatModal; 